import React, { useEffect, useRef } from "react";
import Icon from "components/AppIcon";


const FLOATING_CARDS = [
  { icon: "Gamepad2", label: "Game Generated", sub: "Neon Dungeon Crawler", color: "#6366F1", x: "5%", y: "20%", delay: 0 },
  { icon: "Film", label: "Animation Ready", sub: "Dragon Flight Scene", color: "#F59E0B", x: "80%", y: "15%", delay: 0.6 },
  { icon: "Music", label: "Music Created", sub: "Afrobeat Groove", color: "#10B981", x: "88%", y: "60%", delay: 1.2 },
  { icon: "Video", label: "Video Rendered", sub: "Cinematic Intro", color: "#EC4899", x: "2%", y: "65%", delay: 0.9 },
  { icon: "Mic", label: "Lip Sync Done", sub: "Talking Avatar", color: "#8B5CF6", x: "45%", y: "88%", delay: 1.5 },
];

export default function HeroSection({ onGetStarted, onExplore }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef?.current;
    if (!canvas) return;
    const ctx = canvas?.getContext("2d");
    let animId;
    let particles = [];

    const resize = () => {
      canvas.width = canvas?.offsetWidth;
      canvas.height = canvas?.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    for (let i = 0; i < 60; i++) {
      particles?.push({
        x: Math.random() * canvas?.width,
        y: Math.random() * canvas?.height,
        r: Math.random() * 2 + 0.5,
        dx: (Math.random() - 0.5) * 0.4,
        dy: (Math.random() - 0.5) * 0.4,
        opacity: Math.random() * 0.5 + 0.1,
        color: ["#6366F1", "#8B5CF6", "#F59E0B", "#10B981", "#EC4899"]?.[Math.floor(Math.random() * 5)],
      });
    }

    const draw = () => {
      ctx?.clearRect(0, 0, canvas?.width, canvas?.height);
      particles?.forEach((p) => {
        ctx?.beginPath();
        ctx?.arc(p?.x, p?.y, p?.r, 0, Math.PI * 2);
        ctx.fillStyle = p?.color + Math.floor(p?.opacity * 255)?.toString(16)?.padStart(2, "0");
        ctx?.fill();
        p.x += p?.dx;
        p.y += p?.dy;
        if (p?.x < 0 || p?.x > canvas?.width) p.dx *= -1;
        if (p?.y < 0 || p?.y > canvas?.height) p.dy *= -1;
      });
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <section className="relative flex flex-col items-center text-center py-16 md:py-24 lg:py-28 overflow-hidden min-h-[600px] md:min-h-[680px]">
      {/* Animated canvas background */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{ opacity: 0.6 }}
      />
      {/* Radial glow layers */}
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 90% 60% at 50% 0%, rgba(99,102,241,0.18) 0%, transparent 65%)" }} />
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 60% 40% at 80% 80%, rgba(139,92,246,0.10) 0%, transparent 60%)" }} />
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 50% 35% at 20% 70%, rgba(245,158,11,0.08) 0%, transparent 55%)" }} />
      {/* Floating creation cards */}
      {FLOATING_CARDS?.map((card) => (
        <div
          key={card?.label}
          className="absolute hidden lg:flex items-center gap-2 px-3 py-2 rounded-xl border backdrop-blur-sm"
          style={{
            left: card?.x,
            top: card?.y,
            background: `${card?.color}12`,
            borderColor: `${card?.color}30`,
            animation: `float ${3 + card?.delay}s ease-in-out infinite alternate`,
            animationDelay: `${card?.delay}s`,
            zIndex: 1,
          }}
        >
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: card?.color }}>
            <Icon name={card?.icon} size={14} color="white" />
          </div>
          <div className="text-left">
            <p className="text-xs font-bold" style={{ color: card?.color, fontFamily: "var(--font-heading)", fontSize: 10 }}>{card?.label}</p>
            <p className="text-xs" style={{ color: "var(--color-muted-foreground)", fontFamily: "var(--font-caption)", fontSize: 9 }}>{card?.sub}</p>
          </div>
        </div>
      ))}
      {/* Badge */}
      <div
        className="relative z-10 inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-6 text-xs md:text-sm font-semibold"
        style={{ background: "rgba(99,102,241,0.10)", borderColor: "var(--color-primary)", color: "var(--color-primary)" }}
      >
        <Icon name="Sparkles" size={14} color="var(--color-primary)" />
        Premium AI Creator Platform · 50K+ Creators
      </div>
      {/* Headline */}
      <h1
        className="relative z-10 font-heading font-bold text-3xl md:text-5xl lg:text-6xl xl:text-7xl leading-tight mb-5 max-w-4xl"
        style={{ color: "var(--color-foreground)" }}
      >
        Create{" "}
        <span
          style={{
            background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 40%, #EC4899 80%, #F59E0B 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Music, Videos,
        </span>
        <br />
        <span
          style={{
            background: "linear-gradient(135deg, #10B981 0%, #06B6D4 50%, #6366F1 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          and Games
        </span>{" "}
        with AI in Minutes
      </h1>
      <p
        className="relative z-10 text-sm md:text-base lg:text-lg max-w-2xl mb-8 leading-relaxed"
        style={{ color: "var(--color-muted-foreground)" }}
      >
        One Creator Studio. Unlimited Imagination.
      </p>
      {/* CTA Buttons */}
      <div className="relative z-10 flex flex-col sm:flex-row items-center gap-3 mb-10">
        <button
          onClick={onGetStarted}
          className="flex items-center gap-2 px-7 py-3.5 rounded-2xl text-sm md:text-base font-bold transition-all hover:opacity-90 hover:scale-105 active:scale-95"
          style={{ background: "linear-gradient(135deg, #6366F1, #8B5CF6)", color: "white", boxShadow: "0 8px 32px rgba(99,102,241,0.4)", fontFamily: "var(--font-heading)" }}
        >
          <Icon name="Wand2" size={18} color="white" />
          Start Creating
        </button>
        <button
          onClick={onExplore}
          className="flex items-center gap-2 px-7 py-3.5 rounded-2xl text-sm md:text-base font-bold border transition-all hover:opacity-80"
          style={{ borderColor: "var(--color-border)", background: "var(--color-card)", color: "var(--color-foreground)", fontFamily: "var(--font-heading)" }}
        >
          <Icon name="Play" size={18} color="var(--color-primary)" />
          Explore Creations
        </button>
      </div>
      {/* Stats row */}
      <div className="relative z-10 flex flex-wrap items-center justify-center gap-6 md:gap-10">
        {[
          { icon: "Gamepad2", value: "200K+", label: "Games Created" },
          { icon: "Film", value: "80K+", label: "Animations" },
          { icon: "Music", value: "45K+", label: "Music Tracks" },
          { icon: "Users", value: "50K+", label: "Creators" },
        ]?.map((stat) => (
          <div key={stat?.label} className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "rgba(99,102,241,0.12)" }}>
              <Icon name={stat?.icon} size={15} color="var(--color-primary)" />
            </div>
            <div className="text-left">
              <p className="text-sm font-bold" style={{ color: "var(--color-foreground)", fontFamily: "var(--font-heading)" }}>{stat?.value}</p>
              <p className="text-xs" style={{ color: "var(--color-muted-foreground)", fontFamily: "var(--font-caption)" }}>{stat?.label}</p>
            </div>
          </div>
        ))}
      </div>
      <style>{`
        @keyframes float {
          from { transform: translateY(0px) rotate(-1deg); }
          to { transform: translateY(-12px) rotate(1deg); }
        }
      `}</style>
    </section>
  );
}